import {
    all,
    put,
    select,
    takeLatest,
    call,
} from 'redux-saga/effects';
import {
    CHANGE_CAPACITY,
    CHANGE_COLOR,
    COLOR_KEY,
    SET_ATOMIC_ID,
    SET_DEFAULT_STATE,
} from './constants';
import {
    changeCapacity,
    changeColor,
    setAtomicId,
} from './actions';
import {
    selectAtomicDevice,
    selectAtomicId,
    selectOptionsByAtomicId,
    selectColors,
    selectCurrentCapacity,
    selectDefaultSize,
    selectSizesForColor,
} from './selectors';
import {
    SAILS_PARAM_ATOMIC_ID,
    SAILS_BNT_STORAGE,
    URL_PARAM_ATOMIC_ID,
    SAILS_PARAM_DEVICE_ID,
} from '@vfde-fe/sails-constants';
import { updateStorageSaga } from '@vfde-fe/sails-storage';
import { getAtomicId } from './helpers/getAtomicId';
import { getNewSelectedCapacity } from './helpers/capacityHelper';
import { trackPageView } from './helpers/tracking';
import {
    hasQueryParam,
    updateUrl,
    createUrlWithQueryString,
} from '@vfde-fe/utils';

/**
 * Options saga
 * Watches actions
 */
export default function* optionsSaga () {
    yield takeLatest(SET_DEFAULT_STATE, initColorCapacitySaga);
    yield takeLatest(CHANGE_COLOR, colorChangeSaga);
    yield takeLatest(CHANGE_CAPACITY, capacityChangeSaga);
    yield takeLatest(SET_ATOMIC_ID, setAtomicIdSaga);
}

/**
 * Initialize color and capacity on first load
 */
export function* initColorCapacitySaga () {
    const [colors, defaultSize, atomicId] = yield all([
        select(selectColors()),
        select(selectDefaultSize()),
        call(getAtomicId),
    ]);

    let initialColor;
    let initialCapacity;

    // Set initial options from the atomicId if exist
    if (atomicId) {
        const optionsByAtomic = yield select(selectOptionsByAtomicId(atomicId));
        initialColor = optionsByAtomic.color;
        initialCapacity = optionsByAtomic.capacity;
    }

    // If no valid atomic Id, use default values
    if (!initialColor || !initialCapacity) {
        initialColor = colors[0][COLOR_KEY];
        initialCapacity =  defaultSize;
    }

    yield all([
        put(changeCapacity(initialCapacity)),
        put(changeColor(initialColor)),
    ]);
}

/**
 * Handle changing of colors
 */
export function* colorChangeSaga (action) {
    const [currentCapacity, atomicDevice] = yield all([
        select(selectCurrentCapacity()),
        select(selectAtomicDevice()),
    ]);

    if (atomicDevice) {
        yield put(setAtomicId(atomicDevice.atomicId));
    } else {
        const availableSizes = yield select(selectSizesForColor());

        // Handle case when a previously selected capacity is not available for the selected color
        const newCapacity = getNewSelectedCapacity(availableSizes, currentCapacity);
        yield put(changeCapacity(newCapacity));
    }
}

/**
 * Update atomic ID when capacity change
 */
export function* capacityChangeSaga () {
    yield call(updateAtomicId, {forceRerender: true});
}

/**
 * Update atomic ID when capacity change
 */
export function* updateAtomicId ({forceRerender}) {
    const atomicDevice = yield select(selectAtomicDevice());
    if (atomicDevice) {
        yield put(setAtomicId(atomicDevice.atomicId, forceRerender));
    }
}

/**
 * Set atomic and device Id in session storage
 */
export function* setAtomicIdSaga () {
    const atomicId = yield select(selectAtomicId());
    const {deviceId} = window.additionalPageOptions;

    if (atomicId) {
        yield call(updateStorageSaga, SAILS_BNT_STORAGE, {
            [SAILS_PARAM_ATOMIC_ID]: atomicId,
            [SAILS_PARAM_DEVICE_ID]: deviceId,
        });

        yield trackPageView();

        if (hasQueryParam(URL_PARAM_ATOMIC_ID)) {
            yield call(updateUrl, createUrlWithQueryString(URL_PARAM_ATOMIC_ID, `${atomicId}`), true);
        }
    }
}
